import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
} from 'react';

import HeaderContext from '../context/HeaderContext';
import HeaderNavBarContext from '../context/HeaderNavBarContext';
import HeaderSearchBarContext from '../context/HeaderSearchBarContext';

import fireEvent from '../utils/fireEvent';
import { getUserPostcode, setUserPostcode } from '../utils/userPostcode';
import fetchApI from '../utils/fetchApi';

import TextField from '../../../components/TextField/TextField';
import CustomDropdown from '../../../components/CustomDropdown/CustomDropdown';
import { SecondaryButton } from '../../../components/Button';
import Icon from '../../../components/Icon/Icon';
import Autocomplete from '../../../components/Autocomplete/Autocomplete';
import getModifiedSavedSearches from './utils/getModifiedSavedSearches';
import Checkbox from '../../../components/SelectionInput/Checkbox';
import useMobileSizeDetect from '../hooks/useMobileSizeDetect';

type TSearchBar = {
  setExpandSearchBar: Dispatch<SetStateAction<boolean>>;
  trackGAEvent?: (eventName: string, eventAction: string) => void;
  searchBarTranslationsForTenant?: Record<string, string>;
};

type TSearchSuggestionItem = {
  modifiers: string[];
  value: string;
  searchLink: string;
};

const SearchOnlyThisAdvertiser: React.FC<{
  hasSearchOnPage: boolean;
  isSearchOnPage: boolean;
  setIsSearchOnPage: React.Dispatch<React.SetStateAction<boolean>>;
  translations: any;
}> = ({ hasSearchOnPage, isSearchOnPage, setIsSearchOnPage, translations }) => {
  if (!hasSearchOnPage) return null;

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>) =>
    setIsSearchOnPage((event.target as HTMLInputElement).checked);

  return (
    <Checkbox
      isChecked={isSearchOnPage}
      value={isSearchOnPage?.toString()}
      onChange={handleCheckboxChange}
      labelText={translations.searchFromThisAdvertiser}
    />
  );
};

const SearchButton: React.FC<{ translations: any }> = ({ translations }) => (
  <SecondaryButton type="submit" className="hz-SearchForm-search" data-role="searchForm-button" tabIndex={0}>
    <Icon name="search" className="hz-hide-gte-md" />
    <span className="hz-show-md">{translations.searchButton}</span>
  </SecondaryButton>
);

const SearchBar = ({ setExpandSearchBar, trackGAEvent, searchBarTranslationsForTenant }: TSearchBar) => {
  const { xsrfToken } = useContext<any>(HeaderContext);
  const {
    userDetails: { isLoggedIn },
  } = useContext<any>(HeaderNavBarContext);
  const searchFormRef = useRef<HTMLFormElement>(null);

  const {
    action,
    categoryValue,
    categoryOptions,
    distanceValue,
    distanceOptions,
    hasSearchOnPage,
    searchOnPageValue,
    postcodeValue,
    queryValue,
    recentQueryUrl,
    savedSearchesUrl,
    recentClearUrl,
    suggestionsUrl,
    isSimpleBar,
    translations,
    suggestionRedirectDisabled = false,
    highlightUntypedSuggestions = false,
    showSuggestionFillButton = false,
    isWiderSearchBar = false,
  } = useContext<any>(HeaderSearchBarContext);

  const effectiveHighlightUntypedSuggestions = isWiderSearchBar || highlightUntypedSuggestions;
  const effectiveShowSuggestionFillButton = isWiderSearchBar || showSuggestionFillButton;

  const [isSearchOnPage, setIsSearchOnPage] = useState<boolean>(searchOnPageValue);
  const [category, setCategory] = useState(categoryValue);
  const [distance, setDistance] = useState(distanceValue);
  const [postcode, setPostcode] = useState('');
  const [suggestionList, setSuggestionList] = useState([]);
  const [savedSearchesList, setSavedSearchesList] = useState([]);
  const [autocompleteValue, setAutocompleteValue] = useState(queryValue);
  const [isHardSubmit, setIsHardSubmit] = useState(false);
  const isMobileView = useMobileSizeDetect();

  const handleSearchFormSubmit = useCallback(
    (event) => {
      setUserPostcode(postcode);

      // fix values
      const searchEvent = fireEvent(
        'search',
        {
          category,
          query: autocompleteValue,
          postcode,
          distance,
          onPage: Boolean(isSearchOnPage),
        },
        {
          cancelable: true,
        },
      );

      if (searchEvent.defaultPrevented && event) {
        event.preventDefault();
      } else if (action) {
        searchFormRef.current?.submit();
      }
    },
    [action, postcode, category, autocompleteValue, distance, isSearchOnPage],
  );

  useEffect(() => {
    if (isLoggedIn) {
      const fetchData = async () => {
        try {
          const res = await fetchApI(recentQueryUrl);
          const result = await fetchApI(savedSearchesUrl);
          setSuggestionList(res);
          const modifiedSavedSearchesArr = getModifiedSavedSearches(result);
          setSavedSearchesList(modifiedSavedSearchesArr);
        } catch {}
      };

      fetchData();
    }
  }, [recentQueryUrl, isLoggedIn, savedSearchesUrl]);

  useEffect(() => {
    if (isHardSubmit) {
      handleSearchFormSubmit(undefined);
      setIsHardSubmit(false);
    }
  }, [isHardSubmit, handleSearchFormSubmit]);

  useEffect(() => {
    setAutocompleteValue(queryValue);
  }, [queryValue]);

  useEffect(() => {
    setCategory(categoryValue);
  }, [categoryValue]);

  useEffect(() => {
    const value = getUserPostcode() || postcodeValue;
    setPostcode(value);
  }, [postcodeValue]);

  useEffect(() => {
    setDistance(distanceValue);
  }, [distanceValue]);

  useEffect(() => {
    setIsSearchOnPage(searchOnPageValue);
  }, [searchOnPageValue]);

  const handleSearchQuerySuggestionApply = (item: TSearchSuggestionItem, isSubmit: boolean) => {
    if (trackGAEvent && item.modifiers.length) {
      if (item.modifiers[0] === 'api') {
        trackGAEvent('AutoComplete', 'SuggestionClicked');
      } else if (item.modifiers[0] === 'query') {
        trackGAEvent('AutoComplete', 'SuggestionClicked-Saved');
      } else {
        trackGAEvent('AutoComplete', 'SuggestionClicked-Recent');
      }
    }

    setAutocompleteValue(item.value);
    if (!isSubmit) return;

    if (item.searchLink && !suggestionRedirectDisabled) {
      document.location = item.searchLink;
    } else setIsHardSubmit(isSubmit);
  };

  const handleAutocompleteClick = () => setExpandSearchBar(true);
  const handleAutocompleteChange = (value: string) => setAutocompleteValue(value);
  const handleQueryBuilderClick = () => trackGAEvent?.('AutoComplete', 'QueryBuilderClicked');

  const handlePostcodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const maxLength = 7;
    setPostcode((e.target.value || '').slice(0, maxLength));
  };

  return (
    <div className={`hz-Header-searchBar${isWiderSearchBar ? ' hz-Header-wider-searchBar' : ''}`}>
      <div className={`hz-Header-maxWidth ${isSimpleBar ? 'hz-Header-simple-searchBar' : ''}`}>
        <form
          ref={searchFormRef}
          className="hz-SearchForm"
          method="get"
          action={action}
          onSubmit={handleSearchFormSubmit}
          data-role="searchForm"
        >
          <div className="hz-SearchFieldset-standard">
            <div className="hz-SearchForm-query" data-role="searchForm-query">
              <Autocomplete
                name="query"
                spellCheck={false}
                data-role="searchForm-autocomplete"
                aria-label={translations.queryPlaceholder}
                value={autocompleteValue}
                xsrfToken={xsrfToken}
                suggestionsQueryUrl={suggestionsUrl}
                clearPredefinedSuggestionsUrl={recentClearUrl}
                predefinedSuggestions={suggestionList}
                savedSearches={savedSearchesList}
                suggestionCategory={category}
                onClick={handleAutocompleteClick}
                onFocus={handleAutocompleteClick}
                onChange={handleAutocompleteChange}
                onSelect={handleSearchQuerySuggestionApply}
                onQueryBuilderClick={effectiveShowSuggestionFillButton ? handleQueryBuilderClick : undefined}
                suggestionRedirectDisabled={suggestionRedirectDisabled}
                highlightUntypedSuggestions={effectiveHighlightUntypedSuggestions}
                showSuggestionFillButton={effectiveShowSuggestionFillButton}
                isWiderSearchBar={isWiderSearchBar}
                translations={translations}
                searchBarTranslationsForTenant={searchBarTranslationsForTenant}
              />
            </div>
          </div>
          {isMobileView && <SearchButton translations={translations} />}
          {!isSimpleBar && (
            <div className="hz-SearchFieldset-advanced">
              <div className="hz-SearchForm-category">
                <CustomDropdown
                  id="categoryId"
                  value={category}
                  placeHolderText={translations.categoryPlaceholder}
                  options={categoryOptions}
                  onChange={(e) => setCategory(e.target.value)}
                  data-role="searchForm-category-menu"
                  ariaLabel={translations.categoryPlaceholder}
                />
                {/* Hidden input to ensure category is submitted with form */}
                <input type="hidden" name="categoryId" value={category} />
              </div>
              <div id="formPostcode" className="hz-SearchForm-postcode">
                <TextField
                  labelText=""
                  name="postcode"
                  placeholder={translations.postcodeInputPlaceholder}
                  value={postcode}
                  ariaLabel={translations.postcodeInputPlaceholder}
                  data-role="searchForm-postcode"
                  onChange={handlePostcodeChange}
                />
              </div>
              <div className="hz-SearchForm-distance">
                <CustomDropdown
                  id="distance"
                  value={distance}
                  placeHolderText={translations.distancePlaceholder}
                  ariaLabel={translations.distancePlaceholder}
                  options={distanceOptions}
                  onChange={(e) => setDistance(e.target.value)}
                  data-role="searchForm-distance-menu"
                />
                {/* Hidden input to ensure distance is submitted with form */}
                <input type="hidden" name="distance" value={distance} />
              </div>
            </div>
          )}
          {!isMobileView && <SearchButton translations={translations} />}
          {!isSimpleBar && (
            <div className="hz-SearchForm-options">
              <SearchOnlyThisAdvertiser
                hasSearchOnPage={hasSearchOnPage}
                isSearchOnPage={isSearchOnPage}
                setIsSearchOnPage={setIsSearchOnPage}
                translations={translations}
              />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SearchBar;
